import { useEffect } from 'react';
import { activity } from '../../services/activity';
import './BrandProgress.css';
import { TIER_COLOURS } from './BrandSplash';

/**
 * Link my Food — animated brand loader.
 *
 * A round badge in the style of the Windows loader: an orange arc circles a white track while the
 * brand mark builds itself inside, on a 3.6 s loop —
 *   1. the orange link draws completely,
 *   2. then the white link draws and locks into it,
 *   3. the fork and spoon glide in and the two accent dots pop,
 *   4. the finished mark holds, fades, and the loop restarts.
 *
 * Variants:
 *   inline  badge only, centred with padding (default)
 *   bare    badge only, inline-sized with no padding (used by BrandSpinner)
 *   page    full-viewport loader, e.g. a Suspense fallback
 *   top     slim fixed route-progress bar in the five experience colours (no badge)
 *
 * Props: `value` (0–100, fills the `top` bar only — omit for indeterminate),
 * `label` (announced to screen readers, never shown), `dark` for navy surfaces, `size` badge diameter in px.
 * Honors prefers-reduced-motion with a still, finished mark and a static arc.
 */
const NAVY = '#02142C';
const ORANGE = '#FF7300';
const WHITE = '#FFFFFF';

const Badge = ({ size, dark }) => (
  <svg className="bprog-badge" viewBox="0 0 120 120" width={size} height={size} focusable="false" aria-hidden="true">
    <circle cx="60" cy="60" r="58" fill={dark ? '#0B2447' : NAVY} />
    {/* White track and the orange arc that circles it. */}
    <circle cx="60" cy="60" r="53" fill="none" stroke={WHITE} strokeOpacity=".28" strokeWidth="4" />
    <g className="bprog-spin">
      <circle className="bprog-arc" cx="60" cy="60" r="53" pathLength="100" fill="none" stroke={ORANGE} strokeWidth="4.5" strokeLinecap="round" />
    </g>

    {/* The mark, scaled from its 140 x 136 artwork box and centred in the disc. */}
    <g transform="translate(60 61) scale(0.46) translate(-70 -68)">
      <g className="bprog-mark">
        <g className="bprog-fork" fill={WHITE}>
          <rect x="4.4" y="33" width="3.2" height="27" rx="1.6" />
          <rect x="9.4" y="33" width="3.2" height="27" rx="1.6" />
          <rect x="14.4" y="33" width="3.2" height="27" rx="1.6" />
          <path d="M4.4 57h13.2v4.4a6.6 6.6 0 0 1-13.2 0z" />
          <path d="M9.2 67h3.6l-.72 48a1 1 0 0 1-2.16 0z" />
        </g>
        <rect className="bprog-link bprog-link-a" pathLength="1" x="28" y="4" width="48" height="88" rx="24" fill="none" stroke={ORANGE} strokeWidth="8.5" strokeLinecap="round" />
        <rect className="bprog-link bprog-link-b" pathLength="1" x="52" y="42" width="48" height="88" rx="24" fill="none" stroke={WHITE} strokeWidth="8.5" strokeLinecap="round" />
        <path className="bprog-lock" d="M28 68A24 24 0 0 0 52 92" fill="none" stroke={ORANGE} strokeWidth="8.5" strokeLinecap="round" />
        <g className="bprog-spoon" fill={WHITE}>
          <ellipse cx="124" cy="46" rx="10.2" ry="14.6" />
          <path d="M122.2 60h3.6l-.72 55a1 1 0 0 1-2.16 0z" />
        </g>
        <circle className="bprog-dot bprog-dot-a" cx="92" cy="13" r="4.8" fill={ORANGE} />
        <circle className="bprog-dot bprog-dot-b" cx="36" cy="110" r="5.2" fill={WHITE} />
      </g>
    </g>
  </svg>
);

const TopBar = ({ value }) => {
  const known = typeof value === 'number' && Number.isFinite(value);
  const pct = known ? Math.max(0, Math.min(100, value)) : null;
  return (
    <div className={`bprog-track${known ? '' : ' is-indeterminate'}`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={known ? Math.round(pct) : undefined}>
      <div className="bprog-fill" style={known ? { width: `${pct}%` } : undefined}>
        <span className="bprog-gradient" style={{ '--tiers': `linear-gradient(90deg, ${TIER_COLOURS.join(', ')})` }} />
      </div>
    </div>
  );
};

export default function BrandProgress({ value, label = 'Loading', variant = 'inline', dark = false, size = 120, global = false }) {
  // A visible badge owns the loading state: the global glass loader steps aside while one is mounted.
  const owns = variant !== 'top' && !global;
  useEffect(() => (owns ? activity.mountLocal() : undefined), [owns]);
  if (variant === 'top') {
    return (
      <div className={`bprog bprog-top${dark ? ' is-dark' : ''}`} aria-label={label}>
        <TopBar value={value} />
      </div>
    );
  }
  return (
    <div className={`bprog bprog-${variant}${dark ? ' is-dark' : ''}`} role="status" aria-live="polite" aria-label={label}>
      <div className="bprog-stack">
        <Badge size={size} dark={dark} />
        <span className="bprog-sr">{label}</span>
      </div>
    </div>
  );
}
